import { CaretDown, CaretRight, Receipt } from 'phosphor-react';
import type { BankTransactionRecord, CashReceipt } from '@dill/dill-shared';
import { formatCurrency, formatDate } from '../utils';

/**
 * One approval, shown as one thing.
 *
 * A cash receipt covers every payment approved together — a cheque file banked in bulk is one
 * decision by one person and one deposit in the ERP — and it holds no per-payment detail of its own.
 * So the band is the receipt: the header carries what is true of the whole approval (when, by whom,
 * by what method, and how it went), and the rows beneath it are the payments, read from their
 * remittance records.
 *
 * A single-payment receipt gets a band too. Every receipt is now a list, and giving the one-payment
 * case a different shape would mean two layouts to read instead of one.
 *
 * Presentational only. The rows are the caller's, because a payment reads differently in a review
 * queue than in Post History; what is shared is the frame and the header.
 */
interface ReceiptBandProps {
  receipt: CashReceipt;
  /** The bank credit behind it, for the amount and date the ERP deposit is dated from. */
  deposit: BankTransactionRecord | null;
  /**
   * How many payments the receipt says it covers — from `remittanceIds`, never from the rows.
   *
   * A record that has not loaded still counts. A header that quietly agreed with the visible rows
   * would hide the fact that something is missing, which is the one thing worth knowing.
   */
  recordCount: number;
  /** How many have posted successfully. Only shown when they disagree with the count above. */
  postedCount: number;
  /**
   * Whether this band owns the deposit answer.
   *
   * False when it sits inside a credit-level band: the deposit belongs to the credit and covers
   * both receipts, so repeating it per receipt would show the same answer twice.
   */
  showDeposit?: boolean;
  status?: React.ReactNode;
  action?: React.ReactNode;
  /** What the visible rows add up to, when that is not the receipt's own total. */
  visibleTotal?: number;
  expanded?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}

export const ReceiptBand = ({
  receipt,
  deposit,
  recordCount,
  postedCount,
  showDeposit = true,
  status,
  action,
  visibleTotal,
  expanded = true,
  onToggle,
  children,
}: ReceiptBandProps) => {
  const isPartial = receipt.postStatus === 'PARTIAL';
  const isFailed = receipt.postStatus === 'FAILED';
  // Only when the rows genuinely do not add up. Comparing floats to the cent, because the receipt's
  // total was summed on the server and the rows' here.
  const shortfall = visibleTotal === undefined ? 0 : receipt.amount - visibleTotal;

  const heading = (
    <>
      {onToggle &&
        (expanded ? (
          <CaretDown size={12} weight="bold" className="text-gray-400 shrink-0" />
        ) : (
          <CaretRight size={12} weight="bold" className="text-gray-400 shrink-0" />
        ))}
      <Receipt
        size={15}
        weight="fill"
        className={isFailed ? 'text-red-500' : isPartial ? 'text-orange-500' : 'text-gray-400'}
      />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-900 truncate">
          {formatCurrency(receipt.amount)}
          <span className="ml-1.5 font-normal text-gray-500">
            &middot; {formatDate(receipt.postedAt ?? receipt.createdAt)}
          </span>
          {/* The credit is what dates the ERP deposit, so it is worth naming next to the receipt's
              own date — they are days apart in the normal case. */}
          {deposit?.date && (
            <span className="ml-1.5 font-normal text-gray-400">
              &middot; banked {formatDate(deposit.date)}
            </span>
          )}
        </p>
        <p className="text-[11px] text-gray-500 truncate">
          {recordCount} payment{recordCount === 1 ? '' : 's'}
          {isPartial && (
            <span className="text-orange-700">
              {' '}
              &middot; {postedCount} of {recordCount} posted
            </span>
          )}
          {receipt.postedBy && <span> &middot; {receipt.postedBy}</span>}
          {shortfall > 0.01 && (
            <span className="text-amber-700"> &middot; {formatCurrency(shortfall)} not shown</span>
          )}
          {receipt.erpDepositId && (
            <span className="ml-1.5 font-mono text-gray-400">{receipt.erpDepositId}</span>
          )}
        </p>
      </div>
    </>
  );

  return (
    <div
      className={
        'rounded-lg border bg-white overflow-hidden ' +
        (isFailed ? 'border-red-200' : isPartial ? 'border-orange-200' : 'border-gray-200')
      }>
      <div
        className={
          'flex items-center justify-between gap-3 px-3 py-2 ' +
          (expanded ? 'border-b ' : '') +
          (isFailed
            ? 'border-red-200 bg-red-50/60'
            : isPartial
            ? 'border-orange-200 bg-orange-50/60'
            : 'border-gray-200 bg-gray-50')
        }>
        {/*
          Only the heading is the toggle, never the whole header — the action lives in here too, and
          a button inside a button is invalid markup that swallows the inner click.
        */}
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="flex items-center min-w-0 gap-2 text-left bg-transparent">
            {heading}
          </button>
        ) : (
          <div className="flex items-center min-w-0 gap-2">{heading}</div>
        )}
        <div className="flex items-center gap-2 shrink-0">
          {showDeposit && status}
          {action}
        </div>
      </div>
      {/* The whole-receipt failure, above the payments. A reason that belongs to one payment is on
          its own row; this is the one that stopped all of them — a revoked token, a dead
          connection. */}
      {expanded && isFailed && receipt.postError && (
        <p className="px-3 py-2 text-xs border-b border-red-100 text-red-700 bg-red-50">
          {receipt.postError}
        </p>
      )}
      {expanded && <div className="flex flex-col divide-y divide-gray-100">{children}</div>}
    </div>
  );
};
